<template>
    <div>
        <h6>{{ label('Task') }} Progress:</h6>
        <div class="flex justify-content-start gap-3">
            <div  v-if="!isArchived">
                <custom-tag entity="progress" :id="taskStatus" />
            </div>
            <div  v-else>
                <custom-tag entity="archive" :id="'archive'" />
            </div>
            <div v-if="switchableStatus && !isArchived">
                <Button v-if="!progressDropdown" label="Change Progress" severity="help" size="small" rounded @click="toggleProgressDropdown(true)" />
                <base-dropdown v-else v-model="taskStatus" :options="getAllProgress().filter(p => p.id !== 4)" optionLabel="name" optionValue="id" class="w-full md:w-12rem" :filter="false" :showClear="false" />
            </div>
            <div  v-if="task.assurance_level && isFeatureEnabled">
                <h6>  Progress to completion:</h6>
                <div class="flex justify-content-start gap-3">
                 <span v-if="assuranceLabels[task.assurance_level] || getAssuranceLabel(task.assurance_level)" :class="'assurance-badge badge-' + task.assurance_level" :style="assuranceColors[task.assurance_level] ? { backgroundColor: assuranceColors[task.assurance_level] } : {}">
                    {{ assuranceLabels[task.assurance_level] || getAssuranceLabel(task.assurance_level) }}
                </span>
                </div>
            </div>
            <div v-if="taskStatus == 3 && (authStore.hasPermission(UserPermission.ALLOW_ARCHIVE_TASK) || taskStore.isTaskOwnerOrSelf(task.author_id))">
                <Button
                    v-if="!isArchived"
                    :label="`Archive ${label('Task')}`"
                    icon="pi pi-box"
                    class="p-button-sm"
                    @click="handleToggleArchiveDialog('archive')"
                />

                <Button
                    v-else
                    :label="`Unarchive ${label('Task')}`"
                    icon="pi pi-box"
                    class="p-button-sm"
                    @click="handleToggleArchiveDialog('unarchive')"
                />
            </div>
        </div>
        <div v-if="taskStatus == 2 && isFeatureEnabled">
            <h6>{{ label('Task') }} Progress to Completion:</h6>
            <div class="flex justify-content-start gap-3">
                <base-dropdown
                    v-model="assuranceLevelStatus"
                    :options="assuranceLevelOptions"
                    optionLabel="displayLabel"
                    optionValue="id"
                    class="w-full md:w-12rem"
                    :filter="false"
                    :showClear="false"
                />
                <Button label="Submit" @click="submit" size="small" />
            </div>
        </div>
        <!-- Dialog box for adding comment -->
        <task-completion-comment v-if="commentDialog" :taskId="props.task.id" :is-feature-enabled="isFeatureEnabled" @comment-added="commentAdded" @cancelled="completionCancelled" @hide-dialog="commentDialog = false" />

        <task-archive-reason v-if="showArchiveDialog"  :action-type="archiveState" :taskId="props.task.id" @comment-added="archiveReasonAdded" @cancelled="showArchiveDialog = false" @hide-dialog="showArchiveDialog = false" />
    </div>
</template>

<script setup>
import TaskCompletionComment from './TaskCompletionComment.vue';
import { ref, watch, defineProps, defineEmits, onMounted, computed } from 'vue';
import { taskStore, companyStore, commentStore, authStore } from '../../../../../store/global-stores';
import { getAllAssuranceLevels, getAllProgress } from '../../../constants';
import { storeToRefs } from 'pinia';
const { task, isArchived } = storeToRefs(taskStore);
import { getAssuranceLabel } from '@/product/ams/constants';
import { useAssuranceColors } from '@/composables/useAssuranceColors';
import TaskArchiveReason from '@/product/ams/modules/task/components/TaskArchiveReason.vue';
import { UserPermission } from '@/modules/permission/enum';
import { showToast } from '@/utilities/toast-handler';
import { useLabels } from '@/composables/LabelComposable';

const { label } = useLabels();
const { assuranceColors, assuranceLabels } = useAssuranceColors();
// emits
const emit = defineEmits(['task-status-changed']);
// props
const props = defineProps({
    task: {
        type: Object,
    },
    switchableStatus: {
        type: Boolean,
    },
});

// states
const progressDropdown = ref(false);
const taskStatus = ref(null);
const commentDialog = ref(null);
const showArchiveDialog = ref(null);
const archiveState = ref(null);
const { assuranceLevelStatus } = storeToRefs(taskStore);
const isFeatureEnabled = companyStore.useFeatureAccess('assurance-level');
const assuranceLevelOptions = computed(() =>
    getAllAssuranceLevels().map((level) => ({
        ...level,
        displayLabel: assuranceLabels.value[level.id] || level.label,
    }))
);
const { lastAddedComment } = storeToRefs(commentStore);

onMounted(() => {
    if (props.task.progress) {
        taskStatus.value = props.progress;
    }
});

// watchers
// watch for currentStatus changes in props and updates it into taskStatus
watch(
    () => props.task.progress,
    () => {
        taskStatus.value = props.task.progress;
    }
);
// check for status changes
watch(taskStatus, () => {
    // check for the status provided in the props
    const currentTaskProgress = props.task?.progress;
    // Avoid triggering status changes if
    if (currentTaskProgress !== taskStatus.value) {
        commentDialog.value = true;
    }
    assuranceLevelStatus.value = props.task?.assurance_level;
});


// actions
const toggleProgressDropdown = (flag) => {
    progressDropdown.value = flag;
};

const switchStatus = async () => {
    const bodyParams = {
        status: taskStatus.value,
        tasks: [props.task?.id],
    };
    const statusSwitched = await taskStore.switchTaskStatus(bodyParams);
    if (statusSwitched) {
        emit('task-status-changed', taskStatus.value);
    }
};

const commentAdded = () => {
    switchStatus();
    commentDialog.value = false;
    toggleProgressDropdown(false);
};

const archiveReasonAdded = async () => {
    let lastAddedCommentId = lastAddedComment.value.id;
    taskStore.onArchiveTask(lastAddedCommentId, archiveState, props.task?.id);
    showArchiveDialog.value = false;
};
const completionCancelled = () => {
    // in case completion is cancelled, we will switch back the progress to the current task progress state
    taskStatus.value = props.task.progress;
    commentDialog.value = false;
};
// const archiveDialogCancelled = () => {
//     // in case completion is cancelled, we will switch back the progress to the current task progress state
//     showArchiveDialog.value = false;
// };

const handleToggleArchiveDialog = (type) => {
    archiveState.value = type;
    showArchiveDialog.value = true;
};

const submit = async () => {
    let tempAssurance = assuranceLevelStatus.value;
    if(tempAssurance == null && props.isFeatureEnabled)
    {
        showToast({}, 'Select Progress to completion');
        return true;
    }
    await taskStore.onAssuranceLevelChange(tempAssurance, props.task?.id);
};
</script>

<style lang="scss">


.assurance-badge {
    display: inline-block;
    padding: 0.3rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 999px; /* pill-shaped */
    text-align: center;
    text-transform: capitalize;
    white-space: nowrap;
    color: white;
}

/* Level-specific colors */
.badge-inadequate {
    background-color: #d32f2f; /* Red */
}

.badge-requires_improvement {
    background-color: #c62828; /* Dark Red */
}

.badge-adequate {
    background-color: #f57c00; /* Orange */
}

.badge-good {
    background-color: #43a047; /* Light Green */
}

.badge-outstanding {
    background-color: #2e7d32; /* Dark Green */
}
</style>